import React, { useEffect, useRef, useState } from "react";
import {
  Palette, Image as ImageIcon, Smartphone, Copy, Check, ExternalLink,
  Sparkles, Save, Upload, Rocket, Globe2, Link as LinkIcon, Crown, Clock, Type,
  Settings as SettingsIcon, CreditCard, MapPin, Package as PackageIcon, ShieldCheck, XCircle,
  MessageSquare, Building2, Wallet, Gift,
} from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { SortableList } from "../../components/SortableList";
import FirebaseSection from "../../components/FirebaseSection";
import { TENANT_FONTS, ensureGoogleFont } from "../../lib/fonts";
import { toast } from "sonner";
import { LoadingState } from "../../components/EmptyState";
import { requestLocation } from "../../lib/geolocation";

const PLATFORM_DOMAIN = "localappstore.in";

export default function AdminSettings() {
  const { tenant: authTenant, refresh } = useAuth();
  const [tenant, setTenant] = useState(authTenant);
  const [themes, setThemes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");
  const [form, setForm] = useState({
    name: "", logo_url: "", whatsapp: "", contact_phone: "",
    primary_color: "", secondary_color: "", accent_color: "",
    font_family: "Inter", font_weight_body: 400,
  });
  const [slug, setSlug] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [windows, setWindows] = useState([
    { label: "Morning", slot_start: "05:00", slot_end: "07:30",
      cutoff_start_time: "00:00", cutoff_time: "23:59",
      cutoff_day_offset: -1, is_active: true },
  ]);
  const [autoSlot, setAutoSlot] = useState(false);
  const [ops, setOps] = useState({
    bottle_tracking_enabled: true,
    allow_negative_wallet_balance: true,
    referral_code_at_signup_enabled: true,
    online_payment_enabled: true,
    rider_cancel_enabled: false,
    require_flat_number: false,
    serviceable_apartments_enabled: false,
    allow_manual_address: true,
    maps_provider: "osm",
    google_maps_api_key: "",
    customer_rzp_key_id: "",
    customer_rzp_key_secret: "",
    customer_rzp_webhook_secret: "",
    trial_days_for_subscriptions: 3,
    trial_billing: "free",
    service_areas: "",
    hub_lat: "",
    hub_lng: "",
    service_radius_km: "",
    customer_billing_cycle: "disabled",
    bill_auto_share_channels: ["push"],
  });
  const [locatingHub, setLocatingHub] = useState(false);
  const logoInputRef = useRef(null);

  const refreshTenant = async () => {
    let data;
    try {
      ({ data } = await api.get("/api/admin/tenant/me"));
    } catch (e) {
      toast.error(friendlyMessage(e));
      return;
    }
    setTenant(data.tenant);
    setForm({
      name: data.tenant.name || "",
      logo_url: data.tenant.branding?.logo_url || "",
      whatsapp: data.tenant.branding?.whatsapp || "",
      contact_phone: data.tenant.branding?.contact_phone || "",
      primary_color: data.tenant.branding?.primary_color || "#3A86FF",
      secondary_color: data.tenant.branding?.secondary_color || "#81B29A",
      accent_color: data.tenant.branding?.accent_color || "#E07A5F",
      font_family: data.tenant.branding?.font_family || "Inter",
      font_weight_body: data.tenant.branding?.font_weight_body || 400,
    });
    setSlug(data.tenant.slug || "");
    setCustomDomain(data.tenant.custom_domain || "");
    setOps({
      bottle_tracking_enabled: data.tenant.bottle_tracking_enabled !== false,
      allow_negative_wallet_balance: data.tenant.allow_negative_wallet_balance !== false,
      // Absent on tenants predating the setting — that means ON, today's behaviour.
      referral_code_at_signup_enabled: data.tenant.referral_code_at_signup_enabled !== false,
      online_payment_enabled: data.tenant.online_payment_enabled !== false,
      rider_cancel_enabled: data.tenant.rider_cancel_enabled === true,
      serviceable_apartments_enabled: data.tenant.serviceable_apartments_enabled === true,
      // Defaults ON: a tenant document predating this feature has no key,
      // and manual entry is what they're using today.
      allow_manual_address: data.tenant.allow_manual_address !== false,
      // Off unless switched on (absent on every store predating the setting).
      require_flat_number: data.tenant.require_flat_number === true,
      maps_provider: data.tenant.maps_provider || "osm",
      google_maps_api_key: data.tenant.google_maps_api_key || "",
      customer_rzp_key_id: data.tenant.customer_rzp_key_id || "",
      customer_rzp_key_secret: "",   // never returned
      customer_rzp_webhook_secret: "",   // never returned
      trial_days_for_subscriptions:
        data.tenant.trial_days_for_subscriptions ?? 3,
      trial_billing: data.tenant.trial_billing || "free",
      service_areas: (data.tenant.service_areas || []).join(", "),
      hub_lat: data.tenant.hub_lat != null ? String(data.tenant.hub_lat) : "",
      hub_lng: data.tenant.hub_lng != null ? String(data.tenant.hub_lng) : "",
      service_radius_km: data.tenant.service_radius_km != null ? String(data.tenant.service_radius_km) : "",
      customer_billing_cycle: data.tenant.customer_billing_cycle || "disabled",
      bill_auto_share_channels: data.tenant.bill_auto_share_channels?.length
        ? data.tenant.bill_auto_share_channels : ["push"],
    });
    if (Array.isArray(data.tenant.delivery_windows) && data.tenant.delivery_windows.length) {
      setWindows(data.tenant.delivery_windows.map((w) => ({
        id: w.id, label: w.label || "Morning",
        slot_start: w.slot_start || "05:00",
        slot_end: w.slot_end || "07:30",
        cutoff_start_time: w.cutoff_start_time || "00:00",
        cutoff_time: w.cutoff_time || "23:59",
        cutoff_day_offset: typeof w.cutoff_day_offset === "number" ? w.cutoff_day_offset : -1,
        is_active: w.is_active !== false,
      })));
    }
    setAutoSlot(data.tenant.auto_slot_from_order_time === true);
  };

  useEffect(() => {
    refreshTenant();
    api.get("/api/admin/themes").then(({ data }) => setThemes(data.themes)).catch((e) => toast.error(friendlyMessage(e)));
    // Preload all curated fonts so each card previews in its own typeface
    TENANT_FONTS.forEach((f) => ensureGoogleFont(f.family, f.weights));
  }, []);

  const applyTheme = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/tenant/theme", { key });
      setTenant(data.tenant);
      setForm((f) => ({ ...f,
        primary_color: data.tenant.branding.primary_color,
        secondary_color: data.tenant.branding.secondary_color,
        accent_color: data.tenant.branding.accent_color,
      }));
      toast.success(`Theme applied — refresh the customer app to see it live`);
      refresh && refresh();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const saveBranding = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/api/admin/tenant/branding", form);
      setTenant(data.tenant);
      toast.success("Branding saved");
      refresh && refresh();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const saveOps = async () => {
    setBusy(true);
    try {
      // Only send fields that have a value (don't accidentally clear customer_rzp_key_secret / webhook_secret)
      const payload = { ...ops };
      if (!payload.customer_rzp_key_secret) delete payload.customer_rzp_key_secret;
      if (!payload.customer_rzp_webhook_secret) delete payload.customer_rzp_webhook_secret;
      payload.service_areas = ops.service_areas
        .split(",").map((p) => p.trim()).filter(Boolean);
      // Hub lat/lng/radius are edited as text but stored as floats (or
      // null = "not configured"). Only include them in the request if at
      // least one has a value — otherwise leave them out entirely so an
      // untouched hub config on this tenant is never overwritten just
      // because the admin saved an unrelated field on this same page.
      const toNumOrNull = (s) => (s === "" || s === null || s === undefined ? null : Number(s));
      const hubLat = toNumOrNull(ops.hub_lat), hubLng = toNumOrNull(ops.hub_lng), radius = toNumOrNull(ops.service_radius_km);
      if (hubLat !== null || hubLng !== null || radius !== null) {
        payload.hub_lat = hubLat; payload.hub_lng = hubLng; payload.service_radius_km = radius;
      } else {
        delete payload.hub_lat; delete payload.hub_lng; delete payload.service_radius_km;
      }
      const { data } = await api.patch("/api/admin/tenant/settings", payload);
      setTenant(data.tenant);
      // Re-mask the secret inputs
      setOps((o) => ({
        ...o, customer_rzp_key_secret: "", customer_rzp_webhook_secret: "",
        service_areas: (data.tenant.service_areas || []).join(", "),
        hub_lat: data.tenant.hub_lat != null ? String(data.tenant.hub_lat) : "",
        hub_lng: data.tenant.hub_lng != null ? String(data.tenant.hub_lng) : "",
        service_radius_km: data.tenant.service_radius_km != null ? String(data.tenant.service_radius_km) : "",
      }));
      toast.success("Settings saved");
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const useHubLocation = async () => {
    setLocatingHub(true);
    try {
      const r = await requestLocation();
      if (r.status === "granted" && r.coords) {
        setOps((o) => ({ ...o, hub_lat: String(r.coords.lat), hub_lng: String(r.coords.lng) }));
        toast.success("Location captured — click Save to apply");
      } else {
        toast.error("Couldn't get your location. Please enter it manually.");
      }
    } catch {
      toast.error("Couldn't get your location. Please enter it manually.");
    } finally {
      setLocatingHub(false);
    }
  };

  const toggleOp = async (key) => {
    const nextVal = !ops[key];
    setOps((o) => ({ ...o, [key]: nextVal }));
    setBusy(true);
    try {
      const { data } = await api.patch("/api/admin/tenant/settings", { [key]: nextVal });
      setTenant(data.tenant);
      toast.success("Saved");
      refresh && refresh();
    } catch (e) {
      // Roll back on error
      setOps((o) => ({ ...o, [key]: !nextVal }));
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const toggleBillShareChannel = async (channel) => {
    const has = ops.bill_auto_share_channels.includes(channel);
    const next = has
      ? ops.bill_auto_share_channels.filter((c) => c !== channel)
      : [...ops.bill_auto_share_channels, channel];
    setOps((o) => ({ ...o, bill_auto_share_channels: next }));
    setBusy(true);
    try {
      const { data } = await api.patch("/api/admin/tenant/settings", { bill_auto_share_channels: next });
      setTenant(data.tenant);
      setOps((o) => ({ ...o, bill_auto_share_channels: data.tenant.bill_auto_share_channels || ["push"] }));
      toast.success("Saved");
      refresh && refresh();
    } catch (e) {
      setOps((o) => ({ ...o, bill_auto_share_channels: ops.bill_auto_share_channels }));
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const pickFont = async (family) => {
    // Lazy-load font for instant preview, then persist
    const known = TENANT_FONTS.find((f) => f.family === family);
    ensureGoogleFont(family, known?.weights);
    setForm((f) => ({ ...f, font_family: family }));
    setBusy(true);
    try {
      const { data } = await api.patch("/api/admin/tenant/branding", { font_family: family });
      setTenant(data.tenant);
      toast.success(`Font set to ${family}`);
      refresh && refresh();
    } catch { toast.error("Failed to save font"); } finally { setBusy(false); }
  };

  const pickWeight = async (w) => {
    setForm((f) => ({ ...f, font_weight_body: w }));
    setBusy(true);
    try {
      const { data } = await api.patch("/api/admin/tenant/branding", { font_weight_body: w });
      setTenant(data.tenant);
      toast.success("Weight saved");
      refresh && refresh();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Image files only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "logo");
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = { ...form, logo_url: data.url };
      setForm(next);
      await api.patch("/api/admin/tenant/branding", { logo_url: data.url });
      toast.success("Logo uploaded");
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const saveDomain = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/api/admin/tenant/domain", {
        slug: slug.trim().toLowerCase(),
        custom_domain: customDomain.trim().toLowerCase(),
      });
      setTenant(data.tenant);
      setSlug(data.tenant.slug);
      setCustomDomain(data.tenant.custom_domain || "");
      toast.success("Domain saved");
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const upgradeLive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/tenant/upgrade-live");
      setTenant(data.tenant);
      toast.success("🎉 You're live!");
      refresh && refresh();
    } catch { toast.error("Upgrade failed"); } finally { setBusy(false); }
  };

  const saveDeliveryWindows = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/api/admin/tenant/delivery-windows", {
        windows, auto_slot_from_order_time: autoSlot,
      });
      setTenant(data.tenant);
      toast.success("Delivery slots saved");
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const updateWindow = (i, patch) => {
    setWindows((ws) => ws.map((w, idx) => idx === i ? { ...w, ...patch } : w));
  };
  const addWindow = () => {
    setWindows((ws) => [...ws, {
      label: "Evening", slot_start: "17:00", slot_end: "19:30",
      cutoff_start_time: "00:00", cutoff_time: "14:00",
      cutoff_day_offset: 0, is_active: true,
    }]);
  };
  const removeWindow = (i) => {
    if (windows.length <= 1) { toast.error("Keep at least one delivery slot"); return; }
    setWindows((ws) => ws.filter((_, idx) => idx !== i));
  };

  const reorderWindows = async (next) => {
    setWindows(next);
    try {
      const { data } = await api.put("/api/admin/tenant/delivery-windows", {
        windows: next, auto_slot_from_order_time: autoSlot,
      });
      setTenant(data.tenant);
      toast.success("Slot order saved");
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      refreshTenant();
    }
  };

  if (!tenant) return <LoadingState fullHeight />;

  const isLive = tenant.domain_status === "live";
  const billSharingOn = tenant?.bill_sharing_enabled !== false;
  const previewBase = window.location.origin;
  const pathUrlPlatform = `https://${PLATFORM_DOMAIN}/t/${tenant.slug}`;
  const subdomainPlatform = `https://${tenant.slug}.${PLATFORM_DOMAIN}`;
  const customUrl = tenant.custom_domain ? `https://${tenant.custom_domain}` : "";
  const previewPath = `${previewBase}/t/${tenant.slug}`;
  const manifestUrl = `${previewBase}/manifest.json`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key); setTimeout(() => setCopiedKey(""), 1500);
    toast.success("Copied");
  };

  const activeTheme = themes.find((t) => t.key === tenant.branding?.theme_key);

  return (
    <div className="space-y-8 page-enter" data-testid="admin-settings-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">White-label Console</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Storefront Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 ${
            isLive ? "bg-brand-leaf/10 text-brand-leaf" : "bg-brand-sand/15 text-brand-sand"
          }`} data-testid="domain-status-pill">
            {isLive ? <Crown className="w-3.5 h-3.5"/> : <Sparkles className="w-3.5 h-3.5"/>}
            {isLive ? "Live" : "Trial"}
          </span>
        </div>
      </div>

      {/* Brand identity */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <ImageIcon className="w-5 h-5 text-brand-sky" />
          <h2 className="font-heading font-bold text-lg">Brand identity</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Field label="Brand name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-2xl" data-testid="brand-name-input"/>
            </Field>
            <Field label="Logo">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-brand-cream border border-neutral-200 flex items-center justify-center overflow-hidden shrink-0">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="w-full h-full object-cover" data-testid="logo-preview"/>
                  ) : <ImageIcon className="w-6 h-6 text-neutral-400"/>}
                </div>
                {/* min-w-0 is load-bearing: a flex item defaults to
                    min-width:auto, so without it this column refuses to
                    shrink below the button's unwrapped text width and
                    pushes the whole row past the viewport on mobile. */}
                <div className="flex-1 min-w-0">
                  <input ref={logoInputRef} type="file" accept="image/*" hidden
                         onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input"/>
                  <Button onClick={() => logoInputRef.current?.click()} disabled={busy}
                          variant="outline"
                          className="min-h-12 h-auto py-2.5 rounded-2xl border-2 border-brand-ink font-bold w-full whitespace-normal text-center leading-snug"
                          data-testid="upload-logo-btn">
                    <Upload className="w-4 h-4 mr-2 shrink-0"/> Upload logo (PNG, JPG, SVG · 5MB max)
                  </Button>
                  <div className="text-[11px] text-neutral-500 mt-1 truncate" title={form.logo_url}>{form.logo_url || "No logo uploaded yet"}</div>
                </div>
              </div>
            </Field>
            <Field label="WhatsApp support">
              <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+919999900001" className="h-12 rounded-2xl" data-testid="whatsapp-input"/>
            </Field>
            <Field label="Contact phone">
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+919999900001" className="h-12 rounded-2xl" data-testid="contact-phone-input"/>
            </Field>
          </div>
          {/* Live preview */}
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Live preview</div>
            <div className="rounded-3xl border border-neutral-200 overflow-hidden bg-brand-cream">
              <div className="p-4 flex items-center gap-3 border-b border-neutral-200" style={{ background: form.primary_color, color: "#fff" }}>
                {form.logo_url ? (
                  <img src={form.logo_url} alt="logo" className="w-10 h-10 rounded-xl object-cover bg-white"/>
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center font-heading font-extrabold">{form.name?.[0] || "?"}</div>
                )}
                <div className="flex-1 truncate">
                  <div className="text-xs uppercase tracking-wider opacity-80">Customer App</div>
                  <div className="font-heading font-extrabold truncate">{form.name || "Your Dairy"}</div>
                </div>
              </div>
              <div className="p-4 space-y-2">
                <div className="rounded-xl p-3" style={{ background: form.secondary_color, color: "#fff" }}>
                  <div className="text-xs opacity-80">Wallet Balance</div>
                  <div className="font-heading font-extrabold text-xl">₹820</div>
                </div>
                <div className="rounded-xl p-3" style={{ background: form.accent_color, color: "#fff" }}>
                  <div className="text-xs opacity-80">Today's delivery</div>
                  <div className="font-heading font-bold">Toned Milk × 1 — 5:30 AM</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button onClick={saveBranding} disabled={busy} className="mt-5 h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7" data-testid="save-branding-btn">
          <Save className="w-4 h-4 mr-2"/> Save branding
        </Button>
      </section>

      {/* Operations & Integrations */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="ops-section">
        <div className="flex items-center gap-3 mb-5">
          <SettingsIcon className="w-5 h-5 text-brand-sky"/>
          <h2 className="font-heading font-bold text-lg">Operations & Integrations</h2>
        </div>

        {/* Toggle row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Toggle
            label="Bottle tracking" sub="Hide bottle UI for customers + riders when off"
            value={ops.bottle_tracking_enabled} onChange={() => toggleOp("bottle_tracking_enabled")}
            Icon={PackageIcon} testid="toggle-bottles"
          />
          <Toggle
            label="Allow negative wallet balance"
            sub="Let customers order on wallet with too little balance — the wallet goes negative and you deliver unpaid"
            value={ops.allow_negative_wallet_balance}
            onChange={() => toggleOp("allow_negative_wallet_balance")}
            Icon={Wallet} testid="toggle-allow-negative-wallet"
          />
          <Toggle
            label="Referral code at signup"
            sub="Show the referral code field to new customers and record codes. Off hides it; existing referrals and rewards are unaffected"
            value={ops.referral_code_at_signup_enabled}
            onChange={() => toggleOp("referral_code_at_signup_enabled")}
            Icon={Gift} testid="toggle-referral-at-signup"
          />
          <Toggle
            label="Online payments" sub="Razorpay checkout for customers"
            value={ops.online_payment_enabled} onChange={() => toggleOp("online_payment_enabled")}
            Icon={CreditCard} testid="toggle-online"
          />
          <Toggle
            label="Rider cancellation" sub="Let riders cancel a delivery (not just fail/skip) — off by default"
            value={ops.rider_cancel_enabled} onChange={() => toggleOp("rider_cancel_enabled")}
            Icon={XCircle} testid="toggle-rider-cancel"
          />
        </div>

        {/* Serviceable Apartments — off by default, so an existing store's
            address flow is untouched until an admin opts in here. */}
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-brand-cream/40 p-4" data-testid="apartments-settings-card">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 inline-flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5"/> Serviceable apartments
          </div>
          <div className="text-[11px] text-neutral-500 mt-1.5 mb-3 leading-relaxed">
            Let customers pick their apartment from a list and enter only a flat number, instead of
            typing a full address. Manage the list under <b>Apartments</b> in the sidebar.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Toggle
              label="Enable serviceable apartments"
              sub="Show the apartment picker on the customer address form"
              value={ops.serviceable_apartments_enabled}
              onChange={() => toggleOp("serviceable_apartments_enabled")}
              Icon={Building2} testid="toggle-apartments"
            />
            <Toggle
              label="Allow addresses outside apartments"
              sub="Off = customers can ONLY pick a listed apartment"
              value={ops.allow_manual_address}
              onChange={() => toggleOp("allow_manual_address")}
              Icon={MapPin} testid="toggle-manual-address"
            />
          </div>
          <div className="mt-3">
            <Toggle
              label="Require flat / house number"
              sub="Every new or edited address needs one — shown under the customer's name on Orders, Subscriptions, Deliveries and Customers"
              value={ops.require_flat_number}
              onChange={() => toggleOp("require_flat_number")}
              Icon={Building2} testid="toggle-require-flat"
            />
          </div>
          {ops.serviceable_apartments_enabled && !ops.allow_manual_address && (
            <div className="text-[11px] text-brand-clay font-bold mt-3" data-testid="apartments-only-warning">
              Apartment-only mode: customers who don&rsquo;t live in a listed apartment can&rsquo;t add an address.
            </div>
          )}
        </div>

        {/* Subscription trial — admin sets length AND billing mode */}
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-brand-cream/40 p-4" data-testid="trial-days-card">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 inline-flex items-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5"/> Trial subscriptions (first sub only)
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Length (days)</label>
              <input type="number" min="0" max="30"
                     value={ops.trial_days_for_subscriptions}
                     onChange={(e) => setOps({ ...ops, trial_days_for_subscriptions: parseInt(e.target.value || "0", 10) })}
                     className="mt-1 h-12 w-full rounded-2xl border border-neutral-200 px-4 font-mono text-center"
                     data-testid="trial-days-input"/>
            </div>
            <div>
              <label className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Billing</label>
              <select value={ops.trial_billing}
                      onChange={(e) => setOps({ ...ops, trial_billing: e.target.value })}
                      className="mt-1 h-12 w-full rounded-2xl border border-neutral-200 px-3 bg-white font-bold text-sm"
                      data-testid="trial-billing-select">
                <option value="free">Free (no wallet debit)</option>
                <option value="paid">Paid (normal billing)</option>
              </select>
            </div>
          </div>
          <div className="text-[11px] text-neutral-600 mt-3 leading-relaxed">
            <div><b>Length</b> · how many days the trial runs. Set to <b>0</b> to disable trials.</div>
            <div className="mt-1">
              <b>{ops.trial_billing === "free" ? "Free trial" : "Paid trial"}</b> · {ops.trial_billing === "free"
                ? "deliveries during the trial are not debited from the wallet — customers try the product risk-free."
                : "deliveries during the trial bill normally (wallet/online). The only difference vs. a regular sub is that the subscription auto-cancels at the end of the trial window."}
            </div>
            <div className="mt-1">
              All trial subs <b>auto-cancel</b> when the period ends — customers must explicitly re-subscribe (the next sub is a regular, non-trial one).
              Track conversions in <a href="/admin/trials" className="font-bold text-brand-sky">On Trial</a>.
            </div>
          </div>
        </div>

        {/* Automatic customer billing */}
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-brand-cream/40 p-4" data-testid="auto-billing-card">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 inline-flex items-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5"/> Automatic customer billing
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { v: "disabled", label: "Off" },
              { v: "weekly", label: "Weekly" },
              { v: "monthly", label: "Monthly" },
            ].map((c) => (
              <button key={c.v} type="button"
                      onClick={() => setOps({ ...ops, customer_billing_cycle: c.v })}
                      className={`h-11 rounded-2xl text-sm font-bold border-2 ${ops.customer_billing_cycle === c.v ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                      data-testid={`auto-billing-${c.v}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-neutral-600 mt-3 leading-relaxed">
            {ops.customer_billing_cycle === "disabled" ? (
              <>Off by default — bills only get generated when an admin manually clicks &quot;Bulk-generate&quot; in Reports → Customer invoices.</>
            ) : (
              <>
                Every {ops.customer_billing_cycle === "weekly" ? "Monday" : "1st of the month"}, an invoice is generated for
                every customer with delivered orders in the prior {ops.customer_billing_cycle === "weekly" ? "week (Mon–Sun)" : "calendar month"},
                and a push notification is sent to let them know it&apos;s ready to view in their app.
                Existing invoices are never duplicated for the same period.
              </>
            )}
          </div>
        </div>

        {/* Bill-share channels — the platform owner (super admin) controls
            whether this feature is available to this store at all; when
            they've turned it off, the picker below is disabled rather than
            hidden, so it's clear this is a platform-level restriction, not
            a bug. */}
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-brand-cream/40 p-4" data-testid="bill-share-card">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 inline-flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5"/> Order & bill notifications
          </div>
          {!billSharingOn && (
            <div className="mt-2 text-[11px] font-bold text-brand-clay bg-brand-clay/10 rounded-xl px-3 py-2" data-testid="bill-sharing-disabled-banner">
              This feature has been disabled for your store by the platform owner.
            </div>
          )}
          <div className={`mt-3 flex flex-wrap gap-2 ${!billSharingOn ? "opacity-40 pointer-events-none" : ""}`}>
            {[
              { v: "push", label: "In-app push" },
              { v: "sms", label: "SMS" },
              { v: "whatsapp", label: "WhatsApp" },
            ].map((c) => {
              const on = ops.bill_auto_share_channels.includes(c.v);
              return (
                <button key={c.v} type="button"
                        onClick={() => toggleBillShareChannel(c.v)}
                        disabled={!billSharingOn}
                        className={`h-11 px-4 rounded-2xl text-sm font-bold border-2 ${on ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                        data-testid={`bill-share-${c.v}`}>
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-neutral-600 mt-3 leading-relaxed">
            Channels used for two things: an instant confirmation the moment a customer places an order, and
            the periodic bill/statement generated on the cycle above. SMS/WhatsApp additionally need a
            DLT/WhatsApp template configured before they'll actually send.
          </div>
        </div>

        {/* Maps provider */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5"/> Maps provider
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "osm", label: "OpenStreetMap", sub: "Free · no API key" },
              { v: "google", label: "Google Maps", sub: "Premium · needs API key" },
            ].map((p) => (
              <button key={p.v}
                      onClick={() => setOps({ ...ops, maps_provider: p.v })}
                      className={`text-left rounded-2xl border-2 p-4 transition-all ${ops.maps_provider === p.v ? "border-brand-sky bg-brand-sky/5" : "border-neutral-200 bg-white"}`}
                      data-testid={`maps-${p.v}`}>
                <div className="font-bold">{p.label}</div>
                <div className="text-xs text-neutral-500">{p.sub}</div>
              </button>
            ))}
          </div>
          {ops.maps_provider === "google" && (
            <Input value={ops.google_maps_api_key} onChange={(e) => setOps({ ...ops, google_maps_api_key: e.target.value })}
                   placeholder="Google Maps API key (AIza…)" className="h-12 mt-3 rounded-2xl font-mono text-sm"
                   data-testid="google-maps-key"/>
          )}
        </div>

        {/* Service area */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5"/> Service area (pincodes)
          </div>
          <Input value={ops.service_areas}
                 onChange={(e) => setOps({ ...ops, service_areas: e.target.value })}
                 placeholder="e.g. 500001, 500002, 500081"
                 className="h-12 rounded-2xl font-mono text-sm"
                 data-testid="service-areas-input"/>
          <div className="text-[11px] text-neutral-500 mt-1.5">
            Comma-separated pincodes you actually deliver to. Customers can&rsquo;t add or
            edit an address outside this list. Leave blank to accept any pincode (default).
          </div>
        </div>

        {/* Serviceable radius */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5"/> Serviceable radius (optional)
          </div>
          <div className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
            Works alongside the pincode list above: set a hub location and max delivery distance to
            reject orders that are simply too far away, without maintaining a pincode list. Leave blank to skip this check.
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Input type="number" step="any" value={ops.hub_lat}
                   onChange={(e) => setOps({ ...ops, hub_lat: e.target.value })}
                   placeholder="Hub latitude" className="h-12 rounded-2xl text-sm" data-testid="hub-lat-input"/>
            <Input type="number" step="any" value={ops.hub_lng}
                   onChange={(e) => setOps({ ...ops, hub_lng: e.target.value })}
                   placeholder="Hub longitude" className="h-12 rounded-2xl text-sm" data-testid="hub-lng-input"/>
            <Input type="number" step="any" min="0" value={ops.service_radius_km}
                   onChange={(e) => setOps({ ...ops, service_radius_km: e.target.value })}
                   placeholder="Radius (km)" className="h-12 rounded-2xl text-sm col-span-2 sm:col-span-1"
                   data-testid="service-radius-input"/>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <button type="button" onClick={useHubLocation} disabled={locatingHub}
                    className="text-xs font-bold text-brand-sky inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="use-hub-location-btn">
              <MapPin className="w-3.5 h-3.5"/> {locatingHub ? "Locating…" : "Use my current location"}
            </button>
            {(ops.hub_lat || ops.hub_lng || ops.service_radius_km) && (
              <button type="button"
                      onClick={() => setOps({ ...ops, hub_lat: "", hub_lng: "", service_radius_km: "0" })}
                      className="text-xs font-bold text-neutral-500" data-testid="clear-hub-location-btn">
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Customer-payments Razorpay */}
        <div className="mt-6 rounded-2xl bg-brand-cream p-5 border border-neutral-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="inline-flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-brand-sky"/>
              <div>
                <div className="font-bold">Razorpay (receive from customers)</div>
                <div className="text-xs text-neutral-500">Used when customer pays for orders / recharges their wallet</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {ops.customer_rzp_key_id.startsWith("rzp_live_") ? (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-leaf/10 text-brand-leaf"
                      data-testid="rzp-customer-mode-live">Live mode</span>
              ) : ops.customer_rzp_key_id.startsWith("rzp_test_") ? (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-sand/20 text-amber-700"
                      data-testid="rzp-customer-mode-test">Test mode</span>
              ) : null}
              {tenant?.customer_rzp_secret_set && (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-leaf/10 text-brand-leaf inline-flex items-center gap-1"
                      data-testid="rzp-customer-status">
                  <ShieldCheck className="w-3 h-3"/> Connected
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <Input value={ops.customer_rzp_key_id} onChange={(e) => setOps({ ...ops, customer_rzp_key_id: e.target.value })}
                   placeholder="Key ID (rzp_live_xxx or rzp_test_xxx)" className="h-12 rounded-2xl font-mono text-sm"
                   data-testid="customer-rzp-key-id"/>
            <Input type="password" value={ops.customer_rzp_key_secret} onChange={(e) => setOps({ ...ops, customer_rzp_key_secret: e.target.value })}
                   placeholder={tenant?.customer_rzp_secret_set ? "•••••••• (leave blank to keep)" : "Key Secret"}
                   className="h-12 rounded-2xl font-mono text-sm"
                   data-testid="customer-rzp-key-secret"/>
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            Different from the platform's Razorpay (used for your OTP wallet). Get keys from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline text-brand-sky">dashboard.razorpay.com/app/keys</a>.
            Use a <code className="font-mono">rzp_test_</code> key while testing — orders won't charge real money until you switch to a <code className="font-mono">rzp_live_</code> key.
          </p>

          <div className="mt-4 pt-4 border-t border-neutral-200">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="font-bold text-sm">Webhook (reconciliation)</div>
              {tenant?.customer_rzp_webhook_secret_set && (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-leaf/10 text-brand-leaf"
                      data-testid="rzp-webhook-status">Configured</span>
              )}
            </div>
            <Input type="password" value={ops.customer_rzp_webhook_secret} onChange={(e) => setOps({ ...ops, customer_rzp_webhook_secret: e.target.value })}
                   placeholder={tenant?.customer_rzp_webhook_secret_set ? "•••••••• (leave blank to keep)" : "Webhook secret"}
                   className="h-12 rounded-2xl font-mono text-sm"
                   data-testid="customer-rzp-webhook-secret"/>
            <p className="text-xs text-neutral-500 mt-2">
              Optional but recommended — catches payments whose success callback never reached the app (closed tab, dropped network).
              In Razorpay Dashboard → Webhooks, add <code className="font-mono break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/public/razorpay/webhook`}</code>, subscribe to <code className="font-mono">payment.captured</code>, and paste the secret it gives you here.
            </p>
          </div>
        </div>

        <Button onClick={saveOps} disabled={busy} className="mt-6 h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7" data-testid="save-ops-btn">
          <Save className="w-4 h-4 mr-2"/> Save settings
        </Button>
      </section>

      {/* Firebase / native app push (BYO app configs) */}
      <FirebaseSection />

      {/* Typography */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="typography-section">
        <div className="flex items-center gap-3 mb-1">
          <Type className="w-5 h-5 text-brand-sky" />
          <h2 className="font-heading font-bold text-lg">Typography</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          Pick a Google font for your storefront. Updates live for all customers.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TENANT_FONTS.map((f) => {
            const active = form.font_family === f.family;
            return (
              <button
                key={f.family}
                onClick={() => pickFont(f.family)}
                disabled={busy}
                className={`relative text-left rounded-3xl border-2 p-5 transition-all card-hover ${
                  active ? "border-brand-sky ring-4 ring-brand-sky/20" : "border-neutral-200"
                }`}
                style={{ fontFamily: `'${f.family}', ui-sans-serif, system-ui` }}
                data-testid={`font-${f.family.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
                      {f.desc}
                    </div>
                    <div className="text-2xl font-bold mt-1 leading-tight" style={{ fontWeight: 700 }}>
                      {f.label}
                    </div>
                  </div>
                  {active && <Check className="w-5 h-5 text-brand-leaf shrink-0" />}
                </div>
                <div className="mt-3 space-y-0.5">
                  <div className="text-sm" style={{ fontWeight: 300 }}>Aa — Thin · 300</div>
                  <div className="text-sm" style={{ fontWeight: 400 }}>Aa — Regular · 400</div>
                  <div className="text-sm" style={{ fontWeight: 500 }}>Aa — Medium · 500</div>
                </div>
                <div className="mt-3 text-base text-neutral-700" style={{ fontWeight: form.font_weight_body || 400 }}>
                  The quick brown cow jumps over the milk pail
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Body weight</div>
          <div className="flex gap-2 flex-wrap" data-testid="font-weight-row">
            {[
              { v: 300, l: "Thin" },
              { v: 400, l: "Regular" },
              { v: 500, l: "Medium" },
            ].map((w) => {
              const active = form.font_weight_body === w.v;
              return (
                <button
                  key={w.v}
                  onClick={() => pickWeight(w.v)}
                  disabled={busy}
                  className={`h-11 px-5 rounded-2xl border-2 font-bold text-sm transition-colors ${
                    active ? "text-white border-transparent bg-brand-sky" : "border-neutral-200 bg-white text-neutral-700"
                  }`}
                  style={{ fontWeight: w.v, fontFamily: `'${form.font_family}', ui-sans-serif` }}
                  data-testid={`font-weight-${w.v}`}
                >
                  {w.l} · {w.v}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Delivery windows (multi-slot) */}
      <section id="delivery-slots" className="bg-white rounded-3xl border border-neutral-200 p-6 scroll-mt-24"
               data-testid="delivery-slots-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-brand-sky" />
            <div>
              <h2 className="font-heading font-bold text-lg">Delivery slots &amp; cut-off times</h2>
              <p className="text-xs text-neutral-500">Add multiple slots (e.g. Morning + Evening). Each shows its own ribbon on customer home.</p>
            </div>
          </div>
          <Button onClick={addWindow} disabled={busy || windows.length >= 6} variant="outline"
                  className="h-11 rounded-2xl border-2 border-brand-ink font-bold"
                  data-testid="add-slot-btn">
            + Add slot
          </Button>
        </div>

        {/* Auto-schedule toggle */}
        <label className="flex items-start gap-3 rounded-2xl border-2 border-neutral-200 bg-brand-cream/40 p-3 mb-4 cursor-pointer hover:border-brand-ink transition-colors"
               data-testid="auto-slot-toggle-label">
          <input type="checkbox" checked={autoSlot}
                 onChange={(e) => setAutoSlot(e.target.checked)}
                 className="w-5 h-5 mt-0.5"
                 data-testid="auto-slot-toggle"/>
          <div className="text-sm">
            <div className="font-bold">Auto-assign delivery slot from order time</div>
            <div className="text-xs text-neutral-600 mt-0.5">
              When ON, an order placed by a customer is automatically routed to whichever slot&apos;s <b>Order&nbsp;window</b> covers the current time.
              For example: <i>orders 6&nbsp;PM–midnight → deliver next morning; orders midnight–6&nbsp;PM → deliver same evening.</i>
              Slot-specific rules come from the <b>Order opens</b> and <b>Cut-off time</b> fields below. Doesn&apos;t affect customers who pick a slot manually.
            </div>
          </div>
        </label>

        <SortableList
          items={windows}
          onReorder={reorderWindows}
          keyOf={(w, i) => w.id || `slot-${i}`}
          className="space-y-4"
          testIdPrefix="slot-sort"
          renderItem={(w, handle, i) => (
            <div className="rounded-2xl border-2 p-4"
                 style={{ borderColor: `${form.primary_color}40`, background: `${form.primary_color}06` }}
                 data-testid={`slot-row-${i}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  {handle}
                  <span className="text-xs font-extrabold uppercase tracking-wider px-2 py-1 rounded-full text-white"
                        style={{ background: form.primary_color }}>Slot {i + 1}</span>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-neutral-600 cursor-pointer">
                    <input type="checkbox" checked={w.is_active}
                           onChange={(e) => updateWindow(i, { is_active: e.target.checked })}
                           className="w-3.5 h-3.5" data-testid={`slot-active-${i}`}/>
                    Active
                  </label>
                </div>
                <button onClick={() => removeWindow(i)} disabled={windows.length <= 1}
                        className="text-sm text-brand-clay font-bold disabled:opacity-30"
                        data-testid={`remove-slot-${i}`}>Remove</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Field label="Label">
                  <Input value={w.label} onChange={(e) => updateWindow(i, { label: e.target.value })}
                         placeholder="Morning" className="h-11 rounded-2xl" data-testid={`slot-label-${i}`}/>
                </Field>
                <Field label="Delivery starts">
                  <Input type="time" value={w.slot_start} onChange={(e) => updateWindow(i, { slot_start: e.target.value })}
                         className="h-11 rounded-2xl font-mono" data-testid={`slot-start-${i}`}/>
                </Field>
                <Field label="Delivery ends">
                  <Input type="time" value={w.slot_end} onChange={(e) => updateWindow(i, { slot_end: e.target.value })}
                         className="h-11 rounded-2xl font-mono" data-testid={`slot-end-${i}`}/>
                </Field>
                <Field label="Order opens">
                  <Input type="time" value={w.cutoff_start_time || "00:00"}
                         onChange={(e) => updateWindow(i, { cutoff_start_time: e.target.value })}
                         className="h-11 rounded-2xl font-mono" data-testid={`slot-cutoff-start-${i}`}/>
                </Field>
                <Field label="Cut-off time">
                  <Input type="time" value={w.cutoff_time} onChange={(e) => updateWindow(i, { cutoff_time: e.target.value })}
                         className="h-11 rounded-2xl font-mono" data-testid={`slot-cutoff-${i}`}/>
                </Field>
                <Field label="Cut-off day">
                  <select value={w.cutoff_day_offset ?? -1}
                          onChange={(e) => updateWindow(i, { cutoff_day_offset: parseInt(e.target.value, 10) })}
                          className="h-11 rounded-2xl border border-neutral-200 px-3 w-full bg-white text-sm"
                          data-testid={`slot-cutoff-offset-${i}`}>
                    <option value={0}>Same day</option>
                    <option value={-1}>Previous day (−1)</option>
                    <option value={-2}>2 days before (−2)</option>
                    <option value={-3}>3 days before (−3)</option>
                    <option value={-4}>4 days before (−4)</option>
                    <option value={-5}>5 days before (−5)</option>
                    <option value={-6}>6 days before (−6)</option>
                    <option value={-7}>1 week before (−7)</option>
                  </select>
                </Field>
              </div>
              {/* Preview ribbon */}
              <div className="mt-4 rounded-2xl border-2 px-4 py-2.5 flex items-start gap-3"
                   style={{ borderColor: `${form.primary_color}55`, background: `${form.primary_color}0F` }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: form.primary_color, color: "#fff" }}>
                  <Clock className="w-3.5 h-3.5"/>
                </div>
                <div className="text-sm leading-snug" data-testid={`slot-preview-${i}`}>
                  Accept orders from <b>{prettyTime12(w.cutoff_start_time || "00:00")}</b> to <b>{prettyTime12(w.cutoff_time)}</b>{" "}
                  <b>{describeCutoffOffset(w.cutoff_day_offset ?? -1)}</b>
                  {" "}&amp; we&apos;ll deliver <b>{w.label}</b> ({prettyTime12(w.slot_start)}&nbsp;–&nbsp;{prettyTime12(w.slot_end)})
                </div>
              </div>
            </div>
          )}
        />

        <Button onClick={saveDeliveryWindows} disabled={busy}
                className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7 mt-5"
                data-testid="save-slots-btn">
          <Save className="w-4 h-4 mr-2"/> Save all slots
        </Button>
      </section>

      {/* Theme picker */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Palette className="w-5 h-5 text-brand-sky" />
            <h2 className="font-heading font-bold text-lg">Pick a theme</h2>
          </div>
          {activeTheme && <span className="text-xs px-3 py-1 rounded-full bg-brand-leaf/10 text-brand-leaf font-bold uppercase tracking-wider">Active: {activeTheme.name}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {themes.map((t) => {
            const isActive = tenant.branding?.theme_key === t.key;
            return (
              <button key={t.key} onClick={() => applyTheme(t.key)} disabled={busy}
                      className={`relative text-left rounded-3xl border-2 overflow-hidden transition-all card-hover ${isActive ? "border-brand-sky ring-4 ring-brand-sky/20" : "border-neutral-200"}`}
                      data-testid={`theme-${t.key}`}>
                <div className="p-4" style={{ background: t.preview_bg }}>
                  <div className="flex gap-1.5 mb-3">
                    <div className="w-8 h-8 rounded-xl shrink-0" style={{ background: t.primary_color }}/>
                    <div className="w-8 h-8 rounded-xl shrink-0" style={{ background: t.secondary_color }}/>
                    <div className="w-8 h-8 rounded-xl shrink-0" style={{ background: t.accent_color }}/>
                  </div>
                  <div className="rounded-xl p-2.5 mb-1.5 text-xs text-white font-bold" style={{ background: t.primary_color }}>Subscribe — ₹32</div>
                  <div className="rounded-xl p-2.5 text-xs text-white font-bold" style={{ background: t.secondary_color }}>₹820 wallet</div>
                </div>
                <div className="p-3 bg-white">
                  <div className="font-heading font-bold text-sm flex items-center justify-between">
                    {t.name}
                    {isActive && <Check className="w-4 h-4 text-brand-leaf"/>}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{t.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Domain & Publishing */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Globe2 className="w-5 h-5 text-brand-sky" />
          <h2 className="font-heading font-bold text-lg">Your storefront URLs</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <Field label="Path URL (always works)">
              <UrlRow value={pathUrlPlatform} onCopy={() => copy(pathUrlPlatform, "path")} copied={copiedKey === "path"} tid="url-path"/>
            </Field>
            <Field label="Subdomain URL">
              <UrlRow value={subdomainPlatform} onCopy={() => copy(subdomainPlatform, "sub")} copied={copiedKey === "sub"} tid="url-sub"/>
            </Field>

            <Field label="Your subdomain handle">
              <div className="flex gap-2 items-center">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)}
                       className="h-12 rounded-2xl font-mono text-sm flex-1" data-testid="slug-input"/>
                <span className="text-sm text-neutral-500 font-mono whitespace-nowrap">.{PLATFORM_DOMAIN}</span>
              </div>
            </Field>

            <Field label={<span className="inline-flex items-center gap-2">Custom domain {isLive ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-leaf/10 text-brand-leaf font-bold uppercase">Live only</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-bold uppercase">Upgrade to use</span>}</span>}>
              <Input value={customDomain} onChange={(e) => setCustomDomain(e.target.value)}
                     placeholder="shop.yourdairy.com"
                     disabled={!isLive}
                     className="h-12 rounded-2xl font-mono text-sm disabled:bg-neutral-50 disabled:text-neutral-400"
                     data-testid="custom-domain-input"/>
              {isLive && customDomain && (
                <div className="mt-2 text-xs text-neutral-600">
                  Point a CNAME record from <span className="font-mono font-bold">{customDomain}</span> → <span className="font-mono font-bold">cname.{PLATFORM_DOMAIN}</span>
                </div>
              )}
            </Field>

            {isLive && customUrl && (
              <Field label="Your custom URL">
                <UrlRow value={customUrl} onCopy={() => copy(customUrl, "custom")} copied={copiedKey === "custom"} tid="url-custom"/>
              </Field>
            )}

            <Button onClick={saveDomain} disabled={busy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7" data-testid="save-domain-btn">
              <Save className="w-4 h-4 mr-2"/> Save URLs
            </Button>
          </div>

          {/* Trial / Live card */}
          <div className={`rounded-3xl p-5 ${isLive ? "bg-gradient-to-br from-brand-leaf to-emerald-700 text-white" : "bg-gradient-to-br from-brand-sand to-orange-500 text-white"}`}>
            <div className="flex items-center gap-2 text-white/90 text-xs uppercase tracking-wider font-bold">
              {isLive ? <Crown className="w-4 h-4"/> : <Sparkles className="w-4 h-4"/>}
              {isLive ? "Live account" : "Free trial"}
            </div>
            <div className="font-heading text-2xl font-extrabold mt-1.5">
              {isLive ? "You're publishing live 🎉" : "14-day free trial running"}
            </div>
            <div className="mt-3 text-sm text-white/85">
              {isLive
                ? "Your dairy is fully live. You can link your custom domain anytime."
                : "Your storefront is fully functional during trial. Upgrade to unlock custom domains and remove the trial banner."}
            </div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex items-center gap-2"><Check className="w-4 h-4"/> Path URL: <span className="font-mono">/t/{tenant.slug}</span></li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4"/> Subdomain: <span className="font-mono">{tenant.slug}.{PLATFORM_DOMAIN}</span></li>
              <li className="flex items-center gap-2">
                {isLive ? <Check className="w-4 h-4"/> : <span className="w-4 h-4 opacity-50">○</span>}
                Custom domain
              </li>
            </ul>
            {!isLive && (
              <Button onClick={upgradeLive} disabled={busy}
                      className="mt-5 h-12 rounded-2xl bg-white text-brand-ink hover:bg-white/90 font-black w-full"
                      data-testid="upgrade-live-btn">
                <Rocket className="w-4 h-4 mr-2"/> Upgrade to Live
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* PWA install */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Smartphone className="w-5 h-5 text-brand-sky" />
          <h2 className="font-heading font-bold text-lg">Publish to Play Store / App Store</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Field label="Preview URL (this environment)">
              <UrlRow value={previewPath} onCopy={() => copy(previewPath, "preview")} copied={copiedKey === "preview"} tid="url-preview" external/>
            </Field>
            <Field label="PWA Manifest">
              <Input readOnly value={manifestUrl} className="h-12 rounded-2xl font-mono text-xs" data-testid="manifest-url"/>
            </Field>
          </div>
          <div className="rounded-3xl bg-brand-ink text-white p-5">
            <div className="flex items-center gap-2 mb-3 text-brand-sand">
              <Sparkles className="w-4 h-4"/>
              <span className="text-xs uppercase tracking-wider font-bold">PWABuilder steps</span>
            </div>
            <ol className="space-y-2.5 text-sm text-white/85">
              <li><b>1.</b> Open <a href="https://www.pwabuilder.com" target="_blank" rel="noreferrer" className="underline text-brand-sand">PWABuilder.com</a></li>
              <li><b>2.</b> Paste your subdomain URL and let it scan</li>
              <li><b>3.</b> Download <span className="font-mono">Android (TWA)</span> + <span className="font-mono">iOS</span> packages</li>
              <li><b>4.</b> Sign with your dev keys & upload to Play Console / App Store Connect</li>
              <li><b>5.</b> Approval typically takes 1-3 days</li>
            </ol>
            <a href="https://www.pwabuilder.com" target="_blank" rel="noreferrer">
              <Button className="mt-5 h-12 rounded-2xl bg-white text-brand-ink hover:bg-white/90 font-bold w-full" data-testid="pwabuilder-cta">
                <ExternalLink className="w-4 h-4 mr-2"/> Open PWABuilder
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

function Toggle({ label, sub, value, onChange, Icon, testid }) {
  const on = value;
  return (
    <button onClick={onChange}
            className={`text-left rounded-2xl border-2 p-4 transition-all ${on ? "border-brand-leaf/40 bg-brand-leaf/5" : "border-neutral-200 bg-white"}`}
            data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${on ? "text-brand-leaf" : "text-neutral-400"}`}/>}
          <div className="font-bold text-sm">{label}</div>
        </div>
        <div className={`w-10 h-6 rounded-full transition-colors relative ${on ? "bg-brand-leaf" : "bg-neutral-300"}`}>
          <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-transform ${on ? "translate-x-[18px]" : "translate-x-0.5"}`}/>
        </div>
      </div>
      {sub && <div className="text-[11px] text-neutral-500 mt-1.5">{sub}</div>}
    </button>
  );
}

function prettyTime12(hhmm) {
  if (!hhmm || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":");
  const hi = parseInt(h, 10);
  const period = hi >= 12 ? "PM" : "AM";
  const h12 = (hi % 12) || 12;
  return `${h12}:${m.padStart(2, "0")} ${period}`;
}

function describeCutoffOffset(offset) {
  const n = parseInt(offset, 10) || 0;
  if (n === 0) return "on the day of delivery";
  if (n === -1) return "the day before delivery";
  return `${Math.abs(n)} days before delivery`;
}

function UrlRow({ value, onCopy, copied, tid, external }) {
  return (
    <div className="flex gap-2">
      <Input readOnly value={value} className="h-12 rounded-2xl font-mono text-xs flex-1" data-testid={tid}/>
      <Button onClick={onCopy} variant="outline" className="h-12 rounded-2xl border-2 border-brand-ink font-bold px-4" data-testid={`${tid}-copy`}>
        {copied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
      </Button>
      {external && (
        <a href={value} target="_blank" rel="noreferrer">
          <Button variant="outline" className="h-12 rounded-2xl border-2 border-brand-ink font-bold px-4">
            <ExternalLink className="w-4 h-4"/>
          </Button>
        </a>
      )}
    </div>
  );
}
